import { Node, Edge, Connection } from 'reactflow';

export const mapBackendEdgeToFlow = (e: any): Edge => {
  const categoryObj = e.edge_category && typeof e.edge_category === "object" ? e.edge_category : null;

  const edgeCategoryId =
    typeof e.edge_category === "number"
      ? e.edge_category
      : categoryObj?.id ?? null;

  const colour_primary =
    categoryObj?.colour_primary ??
    e.data?.colour_primary ??
    e.data?.colorprimary ??
    e.style?.stroke ??
    "#000000";

  const colour_text =
    categoryObj?.colour_text ??
    e.data?.colour_text ??
    e.style?.color ??
    "#ffffff";

  return {
    id: String(e.id),
    source: String(e.source),
    target: String(e.target),

    // IMPORTANT: map snake_case → camelCase
    sourceHandle: e.sourceHandle ?? e.source_handle ?? null,
    targetHandle: e.targetHandle ?? e.target_handle ?? null,

    type: e.type || "standardEdge",
    animated: Boolean(e.animated),

    // Canonical edge data keys used by your edge renderer
    data: {
      ...(e.data || {}),
      edge_category: edgeCategoryId, // ✅ always numeric/null
      colour_primary,
      colour_text,
      animated: Boolean(e.animated),
      label: e.data?.label ?? e.label ?? undefined,
    },

    style: {
      ...(e.style || {}),
      stroke: colour_primary,
      color: colour_text,
    },
  };
};


export const isValidConnection = (
  connection: Connection,
  allNodes?: Node[],
): connection is Required<Connection> => {
  if (!connection.source || !connection.target) return false;
  if (!allNodes?.length) return true;
  const sourceNode = allNodes.find(n => n.id === connection.source);
  const targetNode = allNodes.find(n => n.id === connection.target);
  if (!sourceNode || !targetNode) return false;

  let sourceFunc = null;
  if (connection.sourceHandle && sourceNode.data?.functionalities) {
    const funcId = Number(connection.sourceHandle.split('-')[1]);
    sourceFunc = sourceNode.data.functionalities.find((f: any) => f.id === funcId);
  }

  let targetFunc = null;
  if (connection.targetHandle && targetNode.data?.functionalities) {
    const funcId = Number(connection.targetHandle.split('-')[1]);
    targetFunc = targetNode.data.functionalities.find((f: any) => f.id === funcId);
  }

  if (!sourceFunc || !targetFunc) return true;
  const sourceType = Number(sourceFunc.outputEdgeType ?? sourceFunc.outputedgetype);

  const targetType = Number(targetFunc.inputEdgeType ?? targetFunc.input_edge_type);
  return !!sourceType && !!targetType && sourceType === targetType;
};

export const onConnectHandler = async (
  connection: Connection,
  setEdges: React.Dispatch<React.SetStateAction<Edge[]>>,
  allNodes: Node[],
  createEdgeFn: (payload: any) => Promise<any>,
) => {
  if (!isValidConnection(connection, allNodes)) return;

  const sourceNode = allNodes.find(n => n.id === connection.source);
  let matchedFunctionality = null;
  if (sourceNode && sourceNode.data?.functionalities?.length && connection.sourceHandle) {
    const funcId = Number(connection.sourceHandle.split('-')[1]);
    matchedFunctionality = sourceNode.data.functionalities.find((f: any) => f.id === funcId);
  }

  const colorprimary =
    matchedFunctionality?.outputHandleColor ??
    matchedFunctionality?.handle_color ??
    sourceNode?.data?.colorPrimary ??
    sourceNode?.data?.colour_primary ??
    '#ccc';
  
  const edge_category = Number(
    matchedFunctionality?.outputEdgeType ??
    matchedFunctionality?.outputedgetype ??
    1
  );
  const colour_text = sourceNode?.data?.colour_text || "#000";
  const colour_primary = colorprimary; // keep your computed value
  
  const label = matchedFunctionality?.outputLabel || 'New Edge';

  
  

const newEdge: Edge = {
  id: `temp_${Date.now()}`,
  source: connection.source!,
  target: connection.target!,
  sourceHandle: connection.sourceHandle ?? null,
  targetHandle: connection.targetHandle ?? null,
  type: "standardEdge",
  animated: false,
  data: {
    label,
    edge_category,      // ✅ numeric
    colour_primary,     // ✅ matches renderer
    colour_text,
    animated: false,
  },
  style: { stroke: colour_primary, color: colour_text },
};

  setEdges(eds => [...eds, newEdge]);

  try {
    const createdEdge = await createEdgeFn({
      ...newEdge,
      source: Number(newEdge.source),
      target: Number(newEdge.target),
      sourceHandle: newEdge.sourceHandle,
      targetHandle: newEdge.targetHandle,
      style: { stroke: colorprimary, color: colour_text },
      data: { ...newEdge.data, edge_category },
    });
    


    if (createdEdge?.id) {
      setEdges(eds =>
        eds.map(e => (e.id === newEdge.id ? { ...newEdge, id: String(createdEdge.id) } : e)),
      );
    }
  } catch (err) {
    console.error('Failed to create edge:', err);
    setEdges(eds => eds.filter(e => e.id !== newEdge.id));
  }
};

export const onEdgeClickHandler = async (
  edge: Edge,
  setEdges: React.Dispatch<React.SetStateAction<Edge[]>>,
  deleteEdgeFn: (id: number | string) => Promise<any>,
) => {
  setEdges(eds => eds.filter(e => e.id !== edge.id));
  try {
    await deleteEdgeFn(edge.id);
  } catch (error) {
    console.error('Failed to delete edge:', error);
    setEdges(eds => [...eds, edge]);
  }
};
